import { useEffect, useMemo, useState } from "react";
import type { GatewayEndpoint } from "./types";
import {
  AddressHealth,
  GatewayMetaInfo,
  NO_GATEWAY_META,
  allProbed,
  chooseBestAddress,
  endpointKey,
} from "./addresses";
import { fetchGatewayMeta, probeGatewayAddress } from "./meta";

/** A gateway's address facts (`/api/meta`), or "not gateways" while loading / when it has no hub. */
export function useGatewayMeta(hubUrl: string | null | undefined): GatewayMetaInfo {
  const [state, setState] = useState<{ hubUrl: string; meta: GatewayMetaInfo } | null>(null);

  useEffect(() => {
    if (!hubUrl) return;
    let cancelled = false;
    void fetchGatewayMeta(hubUrl).then((meta) => {
      if (!cancelled) setState({ hubUrl, meta });
    });
    return () => {
      cancelled = true;
    };
  }, [hubUrl]);

  // Never hand back another hub's answer while the new one is loading.
  return hubUrl && state?.hubUrl === hubUrl ? state.meta : NO_GATEWAY_META;
}

/**
 * Live state of every gateway address in `endpoints` (probed once per list while `enabled`), plus
 * the address to preselect. `bestIndex` is the advertised first address until every probe has
 * settled, then the best reachable one — so the choice never flickers between partial results.
 */
export function useGatewayAddressHealth(
  endpoints: readonly GatewayEndpoint[],
  scheme: "http" | "https",
  enabled: boolean
): { health: Record<string, AddressHealth>; settled: boolean; bestIndex: number } {
  const [health, setHealth] = useState<Record<string, AddressHealth>>({});
  const listKey = endpoints.map(endpointKey).join(",");

  useEffect(() => {
    if (!enabled || endpoints.length === 0) return;
    let cancelled = false;
    setHealth(Object.fromEntries(endpoints.map((e) => [endpointKey(e), { state: "checking" }])));
    for (const endpoint of endpoints) {
      void probeGatewayAddress(endpoint, scheme).then((result) => {
        if (!cancelled) setHealth((prev) => ({ ...prev, [endpointKey(endpoint)]: result }));
      });
    }
    return () => {
      cancelled = true;
    };
    // `listKey` stands for `endpoints` (a new array each render, same addresses).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey, scheme, enabled]);

  return useMemo(() => {
    const settled = enabled && endpoints.length > 0 && allProbed(endpoints, health);
    return { health, settled, bestIndex: settled ? chooseBestAddress(endpoints, health) : 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey, health, enabled]);
}
